import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import StatusBadge from '../../components/StatusBadge';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { PlusCircle, Trash2, X } from 'lucide-react';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import PriceTag from '../../components/PriceTag';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';

function CancelArticleModal({ article, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);

  const handleCancel = async () => {
    setLoading(true);
    try {
      await API.delete(`/articles/my-articles/${article._id}/cancel`);
      toast.success('Pengajuan artikel berhasil dibatalkan');
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal membatalkan artikel');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ padding: '20px 24px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#111' }}>Batalkan Pengajuan Artikel</div>
            <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>Tindakan ini akan menghapus artikel secara permanen</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', padding: 4 }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ padding: '16px 24px' }}>
          <div style={{ fontSize: 13, color: '#444', background: '#fff8ed', borderRadius: 8, padding: '10px 14px', marginBottom: 14, borderLeft: '3px solid #F59E0B' }}>
            <strong style={{ color: '#111' }}>{article.title || article.what || 'Artikel tanpa judul'}</strong>
          </div>
          <div style={{ fontSize: 13, color: '#DC2626', background: '#FEF2F2', borderRadius: 8, padding: '10px 14px', lineHeight: 1.6 }}>
            <strong>Perhatian:</strong> Artikel yang sudah selesai dibayar <strong>tidak dapat dibatalkan</strong>. Pembatalan hanya tersedia selama pembayaran belum diselesaikan.
          </div>
        </div>
        <div style={{ padding: '0 24px 20px', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="btn btn-secondary btn-sm" disabled={loading}>Kembali</button>
          <button onClick={handleCancel} disabled={loading}
            style={{ background: '#DC2626', color: '#fff', border: 'none', borderRadius: 7, padding: '6px 16px', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1, fontFamily: 'inherit', fontSize: 13 }}>
            {loading ? 'Membatalkan...' : 'Ya, Batalkan'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteRequestModal({ article, onClose, onSuccess }) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await API.patch(`/articles/my-articles/${article._id}/request-delete`, { reason });
      toast.success('Pengajuan penghapusan berhasil dikirim ke admin');
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal mengajukan penghapusan');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.45)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <div style={{
        background: '#fff', borderRadius: 14, width: '100%', maxWidth: 460,
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        <div style={{ padding: '20px 24px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#111' }}>Ajukan Penghapusan Artikel</div>
            <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>Permintaan akan ditinjau oleh admin sebelum dihapus</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '16px 24px' }}>
          <div style={{ fontSize: 13, color: '#444', background: '#f8f8f8', borderRadius: 8, padding: '10px 14px', marginBottom: 16, borderLeft: '3px solid #e2e2e2' }}>
            <strong style={{ color: '#111' }}>{article.title || article.what || 'Artikel tanpa judul'}</strong>
          </div>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#333', display: 'block', marginBottom: 6 }}>
            Alasan penghapusan <span style={{ color: 'var(--ki-text-muted)' }}>*</span>
          </label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Jelaskan mengapa artikel ini ingin dihapus..."
            rows={4}
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8,
              fontSize: 13, fontFamily: 'inherit', resize: 'vertical',
              outline: 'none', color: '#111',
            }}
          />
        </div>

        <div style={{ padding: '0 24px 20px', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} className="btn btn-secondary btn-sm" disabled={loading}>Batal</button>
          <button
            onClick={handleSubmit}
            disabled={!reason.trim() || loading}
            className="btn btn-primary btn-sm"
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              cursor: reason.trim() && !loading ? 'pointer' : 'not-allowed',
              opacity: reason.trim() && !loading ? 1 : 0.5,
            }}
          >
            <Trash2 size={14} /> {loading ? 'Mengirim...' : 'Kirim Pengajuan'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MyArticles() {
  const navigate = useNavigate();
  const { settings } = useSiteSettings();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [payingId, setPayingId] = useState(null);

  const fetchArticles = () => {
    setLoading(true);
    API.get('/articles/my-articles')
      .then(r => { setArticles(r.data.articles || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchArticles(); }, []);

  const waitForSnap = () => new Promise((resolve, reject) => {
    if (window.snap) return resolve();
    let tries = 0;
    const iv = setInterval(() => {
      if (window.snap) { clearInterval(iv); resolve(); }
      else if (++tries > 20) { clearInterval(iv); reject(new Error('Snap timeout')); }
    }, 300);
  });

  const handlePay = async (articleId) => {
    // Mode transfer manual → arahkan ke halaman artikel untuk unggah bukti transfer
    if (settings.paymentMode === 'manual') {
      navigate(`/artikel-saya/${articleId}/review`);
      return;
    }
    setPayingId(articleId);
    try {
      await waitForSnap();
      const res = await API.post('/payments/create', { articleId });
      window.snap.pay(res.data.snapToken, {
        onSuccess: () => { toast.success('Pembayaran berhasil!'); fetchArticles(); },
        onPending: () => { toast('Pembayaran tertunda, cek email Anda.', { icon: '⏳' }); },
        onError:   () => { toast.error('Pembayaran gagal, coba lagi.'); },
        onClose:   () => { toast('Pembayaran dibatalkan.', { icon: '⚠️' }); },
      });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal membuka pembayaran.');
    } finally {
      setPayingId(null);
    }
  };

  return (
    <DashboardLayout title="Artikel Saya" subtitle="Semua artikel yang pernah Anda ajukan">
      {cancelTarget && (
        <CancelArticleModal
          article={cancelTarget}
          onClose={() => setCancelTarget(null)}
          onSuccess={fetchArticles}
        />
      )}
      {deleteTarget && (
        <DeleteRequestModal
          article={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onSuccess={fetchArticles}
        />
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Link to="/publikasi" className="btn btn-primary btn-sm"><PlusCircle size={14} /> Ajukan Baru</Link>
      </div>

      {loading ? (
        <div className="ki-card">{[...Array(4)].map((_, i) => <div key={i} className="shimmer" style={{ height: 52, borderRadius: 8, marginBottom: 10 }} />)}</div>
      ) : articles.length === 0 ? (
        <div className="ki-card" style={{ textAlign: 'center', padding: '60px 24px' }}>
          <div style={{ fontSize: 14, color: 'var(--ki-text-muted)', marginBottom: 16 }}>Belum ada artikel yang diajukan.</div>
          <Link to="/publikasi" className="btn btn-primary">Ajukan Artikel Pertama</Link>
        </div>
      ) : (
        <div className="ki-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="ki-table-wrap">
          <table className="ki-table">
            <thead>
              <tr>
                <th>Judul/Topik</th>
                <th>Kategori</th>
                <th>Status</th>
                <th>Tanggal</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {articles.map(a => (
                <tr key={a._id} style={{ background: a.status === 'payment_pending' ? 'rgba(245,158,11,0.07)' : a.status === 'review_reader' ? 'rgba(245,158,11,0.04)' : a.status === 'deletion_requested' ? 'rgba(220,38,38,0.03)' : '' }}>
                  <td style={{ fontWeight: 600, maxWidth: 280, fontSize: 14 }}>
                    {a.title || a.what}
                    {a.status === 'payment_pending' && <div style={{ fontSize: 11, color: '#D97706', marginTop: 2, fontWeight: 700 }}>Menunggu pembayaran</div>}
                    {a.status === 'payment_pending' && a.payment?.amount > 0 && <div style={{ fontSize: 13, marginTop: 2 }}><PriceTag amount={a.payment.amount} /></div>}
                    {a.status === 'review_reader' && <div style={{ fontSize: 11, color: '#F59E0B', marginTop: 2 }}>Menunggu persetujuan Anda</div>}
                    {a.status === 'deletion_requested' && <div style={{ fontSize: 11, color: 'var(--ki-text-muted)', marginTop: 2 }}>Pengajuan hapus sedang ditinjau admin</div>}
                    {a.status === 'published' && a.revisions?.slice(-1)[0]?.notes?.includes('Pengajuan penghapusan ditolak') && (
                      <div style={{ fontSize: 11, color: '#6B7280', marginTop: 2 }}>Pengajuan hapus ditolak admin · artikel tetap tayang</div>
                    )}
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--ki-text-faint)' }}>{a.category?.name || '-'}</td>
                  <td><StatusBadge status={a.status} /></td>
                  <td style={{ fontSize: 13, color: 'var(--ki-text-faint)' }}>{format(new Date(a.createdAt), 'd MMM yyyy', { locale: id })}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <Link to={"/artikel-saya/" + a._id + "/review"} className="btn btn-secondary btn-sm">Detail</Link>
                      {a.status === 'payment_pending' && (
                        <>
                          <button
                            onClick={() => handlePay(a._id)}
                            disabled={payingId === a._id}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 5,
                              padding: '5px 12px',
                              background: 'rgba(217,119,6,0.12)',
                              border: '1px solid rgba(217,119,6,0.5)',
                              borderRadius: 7,
                              color: '#B45309', fontSize: 12, fontWeight: 700,
                              cursor: payingId === a._id ? 'not-allowed' : 'pointer',
                              fontFamily: 'inherit', opacity: payingId === a._id ? 0.6 : 1,
                            }}
                          >
                            {payingId === a._id ? 'Memproses...' : settings.paymentMode === 'manual' ? 'Bayar (Transfer)' : 'Bayar Sekarang'}
                          </button>
                          <button
                            onClick={() => setCancelTarget(a)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 5,
                              padding: '5px 10px',
                              background: 'none',
                              border: '1px solid rgba(220,38,38,0.4)',
                              borderRadius: 7,
                              color: '#DC2626', fontSize: 12, fontWeight: 600,
                              cursor: 'pointer', fontFamily: 'inherit',
                            }}
                            title="Batalkan pengajuan artikel ini"
                          >
                            Batal
                          </button>
                        </>
                      )}
                      {a.status === 'published' && (
                        <button
                          onClick={() => setDeleteTarget(a)}
                          title="Ajukan penghapusan artikel yang sudah dipublikasi"
                          style={{
                            display: 'flex', alignItems: 'center', gap: 5,
                            padding: '5px 10px', background: 'none',
                            border: '1px solid var(--ki-border)', borderRadius: 7,
                            color: 'var(--ki-text-muted)', fontSize: 12, fontWeight: 600,
                            cursor: 'pointer', fontFamily: 'inherit',
                          }}
                        >
                          <Trash2 size={12} /> Hapus
                        </button>
                      )}
                      {a.status === 'deletion_requested' && (
                        <span style={{ fontSize: 11, color: 'var(--ki-text-muted)', fontStyle: 'italic' }}>Menunggu admin</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
